import {
  Component,
  ElementRef,
  HostListener,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'app-chart-series-select',
  standalone: true,
  imports: [TranslocoPipe],
  template: `
    <div class="relative inline-block">
      <button
        type="button"
        aria-haspopup="listbox"
        [attr.aria-expanded]="open()"
        [attr.aria-label]="'pages.regbr.metrics.seriesSelect.label' | transloco"
        [disabled]="disabled()"
        (click)="toggle()"
        class="inline-flex h-8 items-center gap-1.5 rounded-full border border-border bg-card px-3 text-xs font-medium text-foreground shadow-sm transition hover:border-primary/50 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-border disabled:hover:text-foreground"
      >
        <span>{{
          'pages.regbr.metrics.seriesSelect.selectedItems' | transloco: { count: selected().length }
        }}</span>
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform"
          [class.rotate-180]="open()"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      @if (open()) {
        <div
          role="listbox"
          [attr.aria-label]="'pages.regbr.metrics.seriesSelect.label' | transloco"
          aria-multiselectable="true"
          class="ios-menu ios-menu--light absolute right-0 z-50 mt-2 max-h-72 min-w-[260px] origin-top-right overflow-auto"
        >
          <p class="px-3 py-2 text-xs text-muted-foreground">
            {{
              'pages.regbr.metrics.seriesSelect.limitHint' | transloco: { count: selectionLimit() }
            }}
          </p>
          @for (seriesName of seriesNames(); track seriesName) {
            <button
              type="button"
              role="option"
              [attr.aria-selected]="isSelected(seriesName)"
              [disabled]="isDisabled(seriesName)"
              (click)="toggleSeries(seriesName)"
              class="ios-menu-item disabled:cursor-not-allowed disabled:opacity-40"
            >
              <span
                aria-hidden="true"
                class="flex h-4 w-4 shrink-0 items-center justify-center rounded border border-border"
                [class.bg-primary]="isSelected(seriesName)"
                [class.text-primary-foreground]="isSelected(seriesName)"
              >
                @if (isSelected(seriesName)) {
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="3"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="h-3 w-3"
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                }
              </span>
              <span class="flex-1 text-left">{{ seriesName }}</span>
            </button>
          }
        </div>
      }
    </div>
  `,
})
export class ChartSeriesSelectComponent {
  readonly seriesNames = input<string[]>([]);
  readonly selected = input<string[]>([]);
  readonly selectionLimit = input(5);
  readonly disabled = input(false);
  readonly selectedChange = output<string[]>();

  protected readonly open = signal(false);
  private readonly elementRef = inject(ElementRef);

  protected readonly selectedSet = computed(() => new Set(this.selected()));

  constructor() {
    effect(() => {
      if (this.disabled()) this.open.set(false);
    });
  }

  protected toggle(): void {
    if (this.disabled()) {
      this.open.set(false);
      return;
    }
    this.open.update((value) => !value);
  }

  protected isSelected(seriesName: string): boolean {
    return this.selectedSet().has(seriesName);
  }

  protected isDisabled(seriesName: string): boolean {
    return !this.isSelected(seriesName) && this.selected().length >= this.selectionLimit();
  }

  protected toggleSeries(seriesName: string): void {
    if (this.isDisabled(seriesName)) return;

    const selected = this.selected();
    const next = this.isSelected(seriesName)
      ? selected.filter((name) => name !== seriesName)
      : [...selected, seriesName];

    this.selectedChange.emit(next);
  }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (this.open() && !this.elementRef.nativeElement.contains(event.target)) {
      this.open.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.open.set(false);
  }
}
